import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import Image from 'next/image';
import { getPlayerAvatar } from '@/lib/utils/avatarUtils';
import { AvatarType } from '@/lib/types/common/Avatar';

// Color mapping based on avatar type
export const BADGE_COLORS: Record<AvatarType, string> = {
    tank: '#314491',
    healer: '#3C6229',
    dps: '#603235',
    crafter: '#616161',
    gatherer: '#616161',
    other: '#616161',
};

export interface PlayerAvatarProps {
    avatarId: string;
    /** The duplicate number to display (1, 2, 3, etc.) - only shown if > 0 */
    duplicateNumber?: number;
    /** Size of the avatar in pixels */
    size?: number;
    /** Additional className for the container */
    className?: string;
    /** Additional className for the image */
    imageClassName?: string;
    /** Whether to show the leader icon */
    isLeader?: boolean;
    /** Size of the leader icon in pixels (defaults to size * 0.6) */
    leaderIconSize?: number;
    /** z-index for the overlay. If not provided, will try to inherit from container's stacking context */
    overlayZIndex?: number;
}

/**
 * PlayerAvatar component that displays a player's avatar with an optional
 * duplicate number badge in the bottom-right corner.
 * 
 * The badge color is determined by the avatar type:
 * - Tank: Blue
 * - Healer: Green
 * - DPS: Red
 * - Crafter/Gatherer/Other: Gray
 */
const PlayerAvatar: React.FC<PlayerAvatarProps> = ({
    avatarId,
    duplicateNumber = 0,
    size = 40,
    className = '',
    imageClassName = '',
    isLeader = false,
    leaderIconSize,
    overlayZIndex,
}) => {
    const avatar = getPlayerAvatar(avatarId);
    const showBadge = duplicateNumber > 0;
    const badgeColor = BADGE_COLORS[avatar.type] || BADGE_COLORS.other;
    const containerRef = useRef<HTMLDivElement>(null);
    const [portalRect, setPortalRect] = useState<DOMRect | null>(null);
    const [computedZIndex, setComputedZIndex] = useState<number>(10001);
    const [parentStyles, setParentStyles] = useState<{
        opacity: string;
        transform: string;
        visibility: string;
    }>({ opacity: '1', transform: 'none', visibility: 'visible' });
    
    // Debouncing refs to prevent rapid opacity transitions
    const lastOpacityChangeTime = useRef<number>(0);
    const lastOpacityValue = useRef<number>(1);
    const debouncedOpacity = useRef<number>(1);

    // Calculate badge size based on avatar size
    const badgeSize = Math.max(12, Math.floor(size * 0.5));
    const fontSize = Math.max(8, Math.floor(badgeSize * 0.9));
    const calculatedLeaderIconSize = leaderIconSize ?? Math.max(16, Math.floor(size * 0.6));

    const updatePosition = useCallback(() => {
        if (containerRef.current) {
            const rect = containerRef.current.getBoundingClientRect();
            setPortalRect(rect);

            // Get computed styles from the container and its ancestors
            const containerStyle = window.getComputedStyle(containerRef.current);
            let opacity = containerStyle.opacity;
            let transform = containerStyle.transform;
            let visibility = containerStyle.visibility;

            // Walk up the DOM tree to get inherited styles that affect visibility
            let element: HTMLElement | null = containerRef.current.parentElement;
            while (element) {
                const style = window.getComputedStyle(element);
                
                // Multiply opacities (they compound)
                if (style.opacity !== '1') {
                    opacity = String(parseFloat(opacity) * parseFloat(style.opacity));
                }
                
                // If any parent has visibility: hidden, inherit it
                if (style.visibility === 'hidden') {
                    visibility = 'hidden';
                }
                
                // Combine transforms (this is a simplified approach - in reality transforms are matrix multiplied)
                if (style.transform !== 'none' && transform === 'none') {
                    transform = style.transform;
                } else if (style.transform !== 'none' && transform !== 'none') {
                    // For simplicity, we'll just use the parent's transform if there's a conflict
                    // In a more complex scenario, you'd need to multiply the transform matrices
                    transform = style.transform;
                }
                
                element = element.parentElement;
            }

            // Debounce rapid opacity changes to prevent clipping/blinking
            const currentOpacity = parseFloat(opacity);
            const currentTime = Date.now();
            const opacityDiff = Math.abs(currentOpacity - lastOpacityValue.current);
            
            // Detect if opacity is increasing after decreasing (potential bounce back)
            const isOpacityIncreasing = currentOpacity > lastOpacityValue.current;
            const wasDecreasing = lastOpacityValue.current < debouncedOpacity.current;
            
            // Only update opacity if:
            // 1. Enough time has passed since the last change (debounce), OR
            // 2. The opacity difference is significant (> 0.1), OR  
            // 3. We're going to fully visible (1) or fully invisible (0), OR
            // 4. Opacity is consistently decreasing (not bouncing back up)
            const shouldUpdate = opacityDiff < 0.5;
            
            // Prevent bounce-back: if opacity was going down and suddenly goes up significantly, ignore it
            if (isOpacityIncreasing && wasDecreasing && currentOpacity > 0.5 && debouncedOpacity.current < 0.5) {
                // This looks like a bounce-back, keep the current debounced value
                console.log('Prevented opacity bounce-back from', debouncedOpacity.current, 'to', currentOpacity);
            } else if (shouldUpdate) {
                debouncedOpacity.current = currentOpacity;
                lastOpacityValue.current = currentOpacity;
                lastOpacityChangeTime.current = currentTime;
            }
            
            setParentStyles({ 
                opacity: String(debouncedOpacity.current), 
                transform, 
                visibility 
            });

            // Auto-detect z-index from the stacking context if not explicitly provided
            if (overlayZIndex === undefined) {
                let element: HTMLElement | null = containerRef.current;
                let maxZIndex = 0;
                while (element) {
                    const style = window.getComputedStyle(element);
                    const zIndex = style.zIndex;
                    if (zIndex !== 'auto') {
                        const parsed = parseInt(zIndex, 10);
                        if (!isNaN(parsed) && parsed > maxZIndex) {
                            maxZIndex = parsed;
                        }
                    }
                    element = element.parentElement;
                }
                // Use the found z-index + 1, or default to 10001 if none found
                setComputedZIndex(maxZIndex > 0 ? maxZIndex + 1 : 10001);
            }
        }
    }, [overlayZIndex]);

    // Continuous RAF loop to always keep overlay in sync
    useEffect(() => {
        let animationId: number;
        
        const tick = () => {
            updatePosition();
            animationId = requestAnimationFrame(tick);
        };
        
        animationId = requestAnimationFrame(tick);
        
        return () => {
            cancelAnimationFrame(animationId);
        };
    }, [updatePosition]);

    const renderOverlays = () => {
        if (!portalRect || (!showBadge && !isLeader)) return null;

        const badgeClassName = size <= 25 ? "flex items-center justify-center border border-x-[#c0a270] border-y-[#e0c290] rounded-sm font-bold text-[#e0e2b0] shadow-[0_0_2px_2px_rgba(0,0,0,0.6)]" : "flex items-center justify-center border-2 border-x-[#c0a270] border-y-[#e0c290] rounded-md font-bold text-[#e0e2b0] shadow-[0_0_2px_2px_rgba(0,0,0,0.6)]";

        const effectiveZIndex = overlayZIndex ?? computedZIndex;

        return createPortal(
            <div
                style={{
                    position: 'fixed',
                    top: portalRect.top,
                    left: portalRect.left,
                    width: portalRect.width,
                    height: portalRect.height,
                    pointerEvents: 'none',
                    zIndex: effectiveZIndex,
                    opacity: parentStyles.opacity,
                }}
            >
                {/* Leader Icon */}
                {isLeader && (
                    <div
                        style={{
                            position: 'absolute',
                            left: -calculatedLeaderIconSize * 0.3,
                            top: -calculatedLeaderIconSize * 0.15,
                            width: calculatedLeaderIconSize,
                            height: calculatedLeaderIconSize,
                        }}
                    >
                        <Image
                            src='/leader_icon.png'
                            width={calculatedLeaderIconSize}
                            height={calculatedLeaderIconSize}
                            alt="Game Leader Icon"
                        />
                    </div>
                )}
                {/* Duplicate Number Badge */}
                {showBadge && (
                    <div
                        className={badgeClassName}
                        style={{
                            position: 'absolute',
                            width: badgeSize,
                            height: badgeSize,
                            fontSize: fontSize,
                            backgroundColor: badgeColor,
                            lineHeight: 1,
                            bottom: -3,
                            right: -3,
                        }}
                    >
                        <span className="text-shadow-border shadow-black">
                            {duplicateNumber}
                        </span>
                    </div>
                )}
            </div>,
            document.body
        );
    };

    return (
        <div ref={containerRef} className={`relative ${className}`} style={{ width: size, height: size }}>
            <Image
                src={`/avatar/${avatar.filename}`}
                alt={avatar.name}
                width={size}
                height={size}
                className={`w-full h-full object-cover ${imageClassName}`}
            />
            {renderOverlays()}
        </div>
    );
};

export default PlayerAvatar;

/**
 * Helper function to calculate duplicate numbers for a list of players.
 * Returns a Map of sessionId -> duplicateNumber (0 if no duplicates, 1+ if duplicates exist)
 */
export function calculateDuplicateNumbers(
    players: Array<{ sessionId: string; avatarId: string }>
): Map<string, number> {
    const result = new Map<string, number>();
    const avatarCounts = new Map<string, string[]>(); // avatarId -> array of sessionIds

    // First pass: count players per avatar
    for (const player of players) {
        const existing = avatarCounts.get(player.avatarId) || [];
        existing.push(player.sessionId);
        avatarCounts.set(player.avatarId, existing);
    }

    // Second pass: assign duplicate numbers
    for (const player of players) {
        const playersWithSameAvatar = avatarCounts.get(player.avatarId) || [];
        if (playersWithSameAvatar.length > 1) {
            // Find the index of this player among those with the same avatar
            const index = playersWithSameAvatar.indexOf(player.sessionId);
            result.set(player.sessionId, index + 1);
        } else {
            result.set(player.sessionId, 0);
        }
    }

    return result;
}
